import type { TFunction } from 'i18next';
import type { GetOperationsKpisResponseDto } from '~/app/services/operations-kpi/operations-kpi.types';
import type { RepairQuarterCode } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.types';
import type {
  AccountabilitySiteViewModel,
  AccountabilityViewModel,
  RepairHoursViewModel,
} from './OrganizationManagerDashboardPage.types';
import { formatHoursAmount } from './utils';

const QUARTER_I18N_KEY: Record<RepairQuarterCode, string> = {
  Q1: 'repair.quarters.q1',
  Q2: 'repair.quarters.q2',
  Q3: 'repair.quarters.q3',
  Q4: 'repair.quarters.q4',
};

const QUARTER_YEAR_I18N_KEY: Record<RepairQuarterCode, string> = {
  Q1: 'repair.quarters.q1Year',
  Q2: 'repair.quarters.q2Year',
  Q3: 'repair.quarters.q3Year',
  Q4: 'repair.quarters.q4Year',
};

export function mapOperationsAccountability(
  payload: GetOperationsKpisResponseDto,
  t: TFunction<'dashboard'>,
): AccountabilityViewModel {
  const { accountability } = payload;
  const noData = t('accountability.noData');
  const sites: AccountabilitySiteViewModel[] = accountability.breachSites.map(
    (site) => ({
      id: site.id,
      siteName: site.siteName,
      status: site.status,
      statusLabel: t('accountability.status.breach'),
      detail: t('accountability.breachDetail'),
    }),
  );

  if (
    accountability.compliantSiteCount != null &&
    accountability.compliantSiteCount > 0
  ) {
    sites.push({
      id: 'within-contract-terms',
      siteName: t('accountability.compliantRollup', {
        count: accountability.compliantSiteCount,
      }),
      status: 'compliant',
      statusLabel: t('accountability.status.compliant'),
      detail: t('accountability.withinContractTerms'),
    });
  }

  return {
    activeBreaches:
      accountability.activeBreachCount == null
        ? noData
        : String(accountability.activeBreachCount),
    activeBreachesLabel: t('accountability.activeBreaches'),
    penaltiesCaption: t('accountability.penaltiesUnavailable'),
    slaLabel: t('accountability.slaCompliance'),
    slaRate:
      accountability.slaComplianceRatePercent == null
        ? noData
        : `${accountability.slaComplianceRatePercent}%`,
    slaCaption:
      accountability.compliantSiteCount == null ||
      accountability.siteCount == null
        ? t('accountability.slaCaptionEmpty')
        : t('accountability.slaCaption', {
            compliant: accountability.compliantSiteCount,
            total: accountability.siteCount,
          }),
    sites,
  };
}

export function mapOperationsRepairHours(
  payload: GetOperationsKpisResponseDto,
  t: TFunction<'dashboard'>,
): RepairHoursViewModel {
  const { repairHours } = payload;
  const showYear =
    new Set(repairHours.quarters.map((quarter) => quarter.year)).size > 1;
  const numericHours = repairHours.quarters
    .map((quarter) => quarter.hours)
    .filter((hours): hours is number => hours != null);
  const scaleHours = [
    ...numericHours,
    ...(repairHours.targetHours == null ? [] : [repairHours.targetHours]),
  ];

  return {
    title: t('repair.title'),
    subtitle:
      repairHours.targetHours == null
        ? t('repair.subtitleNoTarget')
        : t('repair.subtitle', { target: repairHours.targetHours }),
    targetHours: repairHours.targetHours,
    targetLineLabel:
      repairHours.targetHours == null
        ? null
        : t('repair.targetLine', { target: repairHours.targetHours }),
    maxHours: scaleHours.length === 0 ? 0 : Math.max(...scaleHours),
    insight: repairInsight(
      repairHours.currentHours,
      repairHours.targetHours,
      t,
    ),
    bars: repairHours.quarters.map((quarter) => ({
      id: `${quarter.year}-${quarter.quarter}`,
      quarter: quarter.quarter,
      label: showYear
        ? t(QUARTER_YEAR_I18N_KEY[quarter.quarter], { year: quarter.year })
        : t(QUARTER_I18N_KEY[quarter.quarter]),
      hours: quarter.hours,
      hoursLabel:
        quarter.hours == null
          ? t('repair.hoursEmpty')
          : t('repair.hoursValue', { hours: formatHoursAmount(quarter.hours) }),
      highlighted: quarter.highlighted,
    })),
  };
}

function repairInsight(
  currentHours: number | null,
  targetHours: number | null,
  t: TFunction<'dashboard'>,
): string | null {
  if (currentHours == null || targetHours == null) return null;

  const current = formatHoursAmount(currentHours);
  const target = formatHoursAmount(targetHours);
  const values = { current, target };

  if (currentHours > targetHours) return t('repair.insightAbove', values);
  if (currentHours < targetHours) return t('repair.insightBelow', values);
  return t('repair.insightEqual', values);
}
